import { computed, inject, Injectable } from '@angular/core';
import { AzureGroup, OrganizationalUnit } from '@common/models';
import { SortOrderByPipe } from '@common/pipes';
import { AzureGroupsService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { OrganizationalUnitsService } from '@common/services/organizational-units.service';
import { getUniqueListByProp } from '@common/services/smart-search';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { UserGroupsTag, UserGroupsTagsMap } from '@common/services/smart-search/user-groups/smart-search-tags-user-groups.model';
import { createFilterOptions, getFilterByContains } from '@common/utils/functions/search';
import { I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';
import { ModelTemplate } from 'mbs-ui-kit';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateUserGroupsService extends SmartSearchTemplatesBase {
  private readonly ability = inject(AbilityService);
  private readonly azureGroupsService = inject(AzureGroupsService);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly orgUnitsService = inject(OrganizationalUnitsService);
  private readonly sortOrderByPipe = inject<SortOrderByPipe<AzureGroup | OrganizationalUnit>>(SortOrderByPipe);

  protected readonly moduleUsers = I18_NAMESPACE_APPS_UI.users;

  protected readonly isGoogle = this.ability.isGoogle;

  protected readonly groupService = computed(() => (this.isGoogle() ? this.orgUnitsService : this.azureGroupsService));
  protected readonly name = computed(() =>
    this.isGoogle() ? UserGroupsTagsMap.get(UserGroupsTag.Path) : UserGroupsTagsMap.get(UserGroupsTag.Name)
  );

  public readonly NameTag: ModelTemplate<AzureGroup | OrganizationalUnit> = {
    tag: this.i18nPipe.transform(this.moduleUsers + this.name().tagKey, { format: 'capitalize' }),
    items: (state): Observable<AzureGroup[] | OrganizationalUnit[]> => {
      const prop = this.name().prop;
      const term = state.leftCaretValue;
      const options = createFilterOptions(term, prop);

      this.groupService().filter = getFilterByContains(options) || '';

      return scheduled(
        this.groupService()
          .fetchGroups()
          .pipe(
            map((res) => res.data),
            map((groups: AzureGroup[] | OrganizationalUnit[]) => {
              const filtered = groups.filter((group) => group[prop]?.toLowerCase()?.includes(term));
              const uniqueList = getUniqueListByProp(filtered, prop).slice(0, 10);

              return this.sortOrderByPipe.transform(uniqueList, prop);
            })
          ),
        asapScheduler
      );
    },
    itemFormatter: (value): string => value[this.name().prop],
    addGroupBrackets: true
  };
}
